import React, { useState, useEffect, useContext } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { AuthContext } from '../App';
import { Trophy, ChevronLeft, Mic, Headphones, Plane, Crown, Medal, Award, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [activeCategory, setActiveCategory] = useState('miles');
  const [leaderboards, setLeaderboards] = useState({});
  const [currentUserStats, setCurrentUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const categories = [
    { id: 'miles', name: 'Toplam Mil', icon: Plane, emoji: '✈️' },
    { id: 'speaking', name: 'Speaking', icon: Mic, emoji: '🎤' },
    { id: 'listening', name: 'Listening', icon: Headphones, emoji: '🎧' },
  ];

  const ranks = [
    { min: 0, name: 'Kabin Adayı', icon: '✈️' },
    { min: 100, name: 'Stajyer', icon: '🎓' },
    { min: 500, name: 'Junior Kabin', icon: '👨‍✈️' },
    { min: 1000, name: 'Kabin Memuru', icon: '🛫' },
    { min: 2500, name: 'Senior Kabin', icon: '⭐' },
    { min: 5000, name: 'Şef Kabin', icon: '👑' },
    { min: 10000, name: 'Elite Crew', icon: '💎' },
  ];

  const getRank = (miles) => {
    for (let i = ranks.length - 1; i >= 0; i--) {
      if (miles >= ranks[i].min) return ranks[i];
    }
    return ranks[0];
  };

  const formatName = (fullName) => {
    if (!fullName) return 'Anonim';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0];
    const firstName = parts[0];
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastInitial}.`;
  };

  // Admin kullanıcıları filtrele
  const filterAdmins = (list) => {
    if (!list) return [];
    return list.filter(u => 
      u.email !== 'gifajans@gmail.com' && 
      u.role !== 'admin' &&
      !u.name?.toLowerCase().includes('fatih şengül')
    );
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setRefreshing(true);
    try {
      const [leaderboardRes, statsRes] = await Promise.all([
        axios.get(`${API}/leaderboard/all`, { withCredentials: true }).catch((err) => {
          console.error('Leaderboard API error:', err);
          return { data: {} };
        }),
        axios.get(`${API}/gamification/profile`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);

      console.log('Leaderboard API response:', leaderboardRes.data);
      
      const realData = leaderboardRes.data || {};
      
      // Admin kullanıcıları filtrele ve sırala
      const mergedData = {
        miles: filterAdmins(realData.miles || []).sort((a, b) => (b.total_miles || 0) - (a.total_miles || 0)),
        speaking: filterAdmins(realData.speaking || []).sort((a, b) => (b.count || 0) - (a.count || 0)),
        listening: filterAdmins(realData.listening || []).sort((a, b) => (b.count || 0) - (a.count || 0)),
      };
      
      console.log('Processed leaderboard data:', mergedData);
      
      setLeaderboards(mergedData);
      setCurrentUserStats(statsRes.data);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getCurrentUserPosition = (list) => {
    if (!list || !user) return null;
    const idx = list.findIndex(u => u.user_id === user.id || u.id === user.id);
    return idx >= 0 ? idx + 1 : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-400/30 border-t-blue-400 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-blue-200">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const activeData = leaderboards[activeCategory] || [];
  const myPosition = getCurrentUserPosition(activeData);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-950">
      {/* Header */}
      <div className="bg-blue-800/50 border-b border-blue-700/50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="flex items-center gap-2 text-blue-200 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Geri</span>
            </button>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-400" />
              Liderlik Tablosu
            </h1>
            <button 
              onClick={loadAllData}
              disabled={refreshing}
              className="flex items-center gap-1 text-blue-200 hover:text-white transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* User Stats Card */}
        {currentUserStats && (
          <Card className="mb-6 bg-blue-800/40 border-blue-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-xl font-bold text-white">
                    {user?.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <p className="font-semibold text-white">{user?.name}</p>
                    <p className="text-blue-300 text-sm">
                      {getRank(currentUserStats.total_miles || 0).icon} {getRank(currentUserStats.total_miles || 0).name}
                    </p>
                  </div>
                </div>
                <div className="flex gap-8">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{(currentUserStats.total_miles || 0).toLocaleString()}</p>
                    <p className="text-xs text-blue-300">Mil</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-yellow-400">#{myPosition || '-'}</p>
                    <p className="text-xs text-blue-300">Sıralama</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Category Tabs */}
        <div className="flex gap-2 mb-6">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                activeCategory === cat.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-blue-800/40 text-blue-200 hover:bg-blue-700/50'
              }`}
            >
              <span>{cat.emoji}</span>
              <span className="hidden sm:inline">{cat.name}</span>
            </button>
          ))}
        </div>

        {/* Leaderboard Card */}
        <Card className="bg-blue-800/30 border-blue-700/50">
          <CardContent className="p-0">
            {/* Top 3 - Podium Style */}
            {activeData.length >= 3 && (
              <div className="p-6 border-b border-blue-700/30">
                <div className="flex items-end justify-center gap-4">
                  {/* 2nd Place */}
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 rounded-full bg-gray-400 flex items-center justify-center text-xl font-bold text-white mb-2 relative overflow-hidden">
                      {activeData[1]?.profile_photo ? (
                        <img src={activeData[1].profile_photo} alt="" className="w-full h-full object-cover" />
                      ) : (
                        activeData[1]?.name?.charAt(0)
                      )}
                      <Medal className="absolute -top-1 -right-1 w-5 h-5 text-gray-300" />
                    </div>
                    <p className="text-white font-medium text-sm truncate max-w-[80px]">{formatName(activeData[1]?.name)}</p>
                    <p className="text-blue-300 text-xs">
                      {activeCategory === 'miles' 
                        ? `${(activeData[1]?.total_miles || 0).toLocaleString()} mil`
                        : `${activeData[1]?.count || 0} ders`
                      }
                    </p>
                    <div className="h-16 w-20 bg-gray-500/50 rounded-t-lg mt-2 flex items-center justify-center">
                      <span className="text-2xl font-bold text-gray-300">2</span>
                    </div>
                  </div>

                  {/* 1st Place */}
                  <div className="flex flex-col items-center -mt-4">
                    <Crown className="w-8 h-8 text-yellow-400 mb-1" />
                    <div className="w-16 h-16 rounded-full bg-yellow-500 flex items-center justify-center text-2xl font-bold text-white mb-2 overflow-hidden ring-4 ring-yellow-400">
                      {activeData[0]?.profile_photo ? (
                        <img src={activeData[0].profile_photo} alt="" className="w-full h-full object-cover" />
                      ) : (
                        activeData[0]?.name?.charAt(0)
                      )}
                    </div>
                    <p className="text-white font-semibold truncate max-w-[100px]">{formatName(activeData[0]?.name)}</p>
                    <p className="text-yellow-300 text-sm font-medium">
                      {activeCategory === 'miles' 
                        ? `${(activeData[0]?.total_miles || 0).toLocaleString()} mil`
                        : `${activeData[0]?.count || 0} ders`
                      }
                    </p>
                    <div className="h-24 w-24 bg-yellow-500/30 rounded-t-lg mt-2 flex items-center justify-center">
                      <span className="text-3xl font-bold text-yellow-400">1</span>
                    </div>
                  </div>

                  {/* 3rd Place */}
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 rounded-full bg-amber-600 flex items-center justify-center text-xl font-bold text-white mb-2 relative overflow-hidden">
                      {activeData[2]?.profile_photo ? (
                        <img src={activeData[2].profile_photo} alt="" className="w-full h-full object-cover" />
                      ) : (
                        activeData[2]?.name?.charAt(0)
                      )}
                      <Award className="absolute -top-1 -right-1 w-5 h-5 text-amber-400" />
                    </div>
                    <p className="text-white font-medium text-sm truncate max-w-[80px]">{formatName(activeData[2]?.name)}</p>
                    <p className="text-blue-300 text-xs">
                      {activeCategory === 'miles' 
                        ? `${(activeData[2]?.total_miles || 0).toLocaleString()} mil`
                        : `${activeData[2]?.count || 0} ders`
                      }
                    </p>
                    <div className="h-12 w-20 bg-amber-600/30 rounded-t-lg mt-2 flex items-center justify-center">
                      <span className="text-2xl font-bold text-amber-500">3</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Full List */}
            <div className="divide-y divide-blue-700/30 max-h-[500px] overflow-y-auto">
              {activeData.slice(3).map((entry, idx) => {
                const position = idx + 4;
                const isCurrentUser = entry.user_id === user?.id || entry.id === user?.id;
                const rank = getRank(entry.total_miles || 0);
                
                return (
                  <div 
                    key={entry.user_id || entry.id || idx}
                    className={`flex items-center gap-4 px-4 py-3 transition-colors ${
                      isCurrentUser 
                        ? 'bg-blue-600/30 border-l-4 border-blue-400' 
                        : 'hover:bg-blue-800/30'
                    }`}
                  >
                    <div className={`w-8 text-center font-bold ${
                      isCurrentUser ? 'text-blue-300' : 'text-blue-400'
                    }`}>
                      {position}
                    </div>
                    
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold overflow-hidden ${
                      isCurrentUser ? 'ring-2 ring-blue-400' : ''
                    }`}>
                      {entry.profile_photo ? (
                        <img src={entry.profile_photo} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center ${
                          isCurrentUser ? 'bg-blue-500 text-white' : 'bg-blue-700/50 text-blue-200'
                        }`}>
                          {entry.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${isCurrentUser ? 'text-white' : 'text-blue-100'}`}>
                        {isCurrentUser ? `${formatName(entry.name)} (Sen)` : formatName(entry.name)}
                      </p>
                      <p className="text-xs text-blue-400">{rank.icon} {rank.name}</p>
                    </div>
                    
                    <div className={`text-right font-semibold ${isCurrentUser ? 'text-blue-200' : 'text-blue-300'}`}>
                      {activeCategory === 'miles' 
                        ? `${(entry.total_miles || 0).toLocaleString()} mil`
                        : `${entry.count || 0} ders`
                      }
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Empty State */}
            {activeData.length === 0 && (
              <div className="p-12 text-center">
                <Trophy className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <p className="text-blue-300">Henüz bu kategoride veri yok</p>
                <p className="text-blue-400 text-sm mt-2">Aktivite yaparak listeye girebilirsiniz!</p>
              </div>
            )}

            {/* Total Count */}
            {activeData.length > 0 && (
              <div className="px-4 py-3 bg-blue-900/30 border-t border-blue-700/30">
                <p className="text-center text-blue-400 text-sm">
                  Toplam {activeData.length} öğrenci
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LeaderboardPage;
